// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import { CircleX, User, Package, Calendar, Users, Loader2 } from "lucide-react";

const BookingCard = ({
  booking,
  isSelected,
  onClick,
  openDetails,
  selectionMode,
  isCheckboxSelected = false,
  onToggleSelection,
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
      case "confirmed":
      case "paid":
        return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300";
      case "pending":
      case "waiting":
      case "payment-pending":
        return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300";
      case "draft":
      case "submitted":
      case "processed":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300";
      case "rejected":
      case "canceled":
      case "payment-error":
        return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
      case "on-hold":
      case "postponed":
        return "bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200";
      default:
        return "bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200";
    }
  };

  const getLlmLabelInfo = (llmLabel) => {
    switch (llmLabel) {
      case "U":
        return {
          label: "Unbedenklich",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700",
        };
      case "E":
        return {
          label: "Explizit problematisch",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-700",
        };
      case "V":
        return {
          label: "Subtile/kodierte Hinweise",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700",
        };
      default:
        return {
          label: "N/A",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-gray-100 dark:bg-slate-600 text-gray-800 dark:text-slate-200 border-gray-200 dark:border-slate-500",
        };
    }
  };

  const getUserDisplayName = () => {
    if (booking.user) {
      if (booking.user.first_name && booking.user.last_name) {
        return `${booking.user.first_name} ${booking.user.last_name}`;
      }
      if (booking.user.first_name) {
        return booking.user.first_name;
      }
      if (booking.user.last_name) {
        return booking.user.last_name;
      }
      if (booking.user.name) {
        return booking.user.name;
      }
    }
    return booking.contactPerson || booking.email || "N/A";
  };

  const formatDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return null;
    const dd = d.getDate();
    const mm = d.getMonth() + 1;
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
  };
  const formatDateRange = (timeSlot) => {
    if (!timeSlot) return "N/A";
    const formattedDate = formatDateTime(timeSlot);
    return formattedDate || "N/A";
  };
  // Only show actual LLM label when a run has completed (llm_general set to true/false or 0/1)
  const hasValidLlmResults =
    booking.llm_general === true ||
    booking.llm_general === false ||
    booking.llm_general === 0 ||
    booking.llm_general === 1;
  const llmLabelInfo = getLlmLabelInfo(booking.llm_label);

  // Display total from backend only
  const displayTotal = booking.total;

  const handleCheckboxClick = (e) => {
    e.stopPropagation(); // Prevent card click when clicking checkbox
    if (onToggleSelection) {
      onToggleSelection(booking.uuid);
    }
  };

  return (
    <div className="relative">
      <div
        className={`border-2 rounded-xl md:p-5 p-3 transition-all duration-200 ${
          selectionMode
            ? isCheckboxSelected
              ? "border-primary-color dark:border-primary-color/40 bg-gradient-primary-secondary-tint shadow-md"
              : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800"
            : isSelected
              ? "hover:shadow-md border-primary-color dark:border-primary-color/40 bg-gradient-primary-secondary-tint shadow-lg shadow-primary-color/10 cursor-pointer"
              : "border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer bg-white dark:bg-slate-800"
        }`}
        onClick={!selectionMode && onClick ? () => onClick(booking) : undefined}
      >
        <div
          className={`flex flex-wrap justify-between items-start ${openDetails ? "gap-0" : "gap-2"}`}
        >
          <div className="flex-1">
            <div
              className={`flex md:flex-row flex-col items-start gap-2 mb-2 ${openDetails ? "justify-between" : "justify-start"}`}
            >
              <div className="flex gap-1 items-start flex-col">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {booking.title || "N/A"}
                </h4>
                {openDetails &&
                  (hasValidLlmResults ? (
                    <div className={`${llmLabelInfo.className}`}>
                      <CircleX className="lucide lucide-circle-x w-3 h-3 mr-1" />
                      {llmLabelInfo.label}
                    </div>
                  ) : (
                    <div className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-500">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      processing...
                    </div>
                  ))}
              </div>
              <div className="flex gap-1 items-end flex-col">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border ${getStatusColor(booking.status)}`}
                >
                  {booking.status || "draft"}
                </span>
                {openDetails && (
                  <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    €{displayTotal || "N/A"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap md:justify-between justify-start items-center gap-x-2 gap-y-0 mb-2">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-0 font-mono">
                {booking.bookingNumber || "N/A"}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-0 font-mono">
                {formatDateRange(booking.booking_time)}
              </p>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
              <span className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                <User className="lucide lucide-user w-4 h-4 inline mr-2" />
                {getUserDisplayName()}
              </span>
              {booking.organization?.name && (
                <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-2 font-mono">
                  <Users className="w-4 h-4" />
                  {booking.organization.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {booking.timeSlot?.startTime
                  ? new Date(booking.timeSlot.startTime).toLocaleDateString()
                  : "No date"}
              </span>
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                {booking.participants || "N/A"}
              </span>
              <span className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Paket {booking.package || "N/A"}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            {openDetails !== true && (
              <>
                {hasValidLlmResults ? (
                  <div
                    className={`${llmLabelInfo.className} sm-only:flex hidden`}
                  >
                    <CircleX className="lucide lucide-circle-x w-3 h-3 mr-1" />
                    {llmLabelInfo.label}
                  </div>
                ) : (
                  <div className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-500 sm-only:flex hidden">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    processing...
                  </div>
                )}
                <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  €{displayTotal || "N/A"}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {selectionMode && (
        <div
          className="absolute top-0 left-0 flex p-4 justify-end items-end w-full h-full z-10 cursor-pointer"
          onClick={handleCheckboxClick}
        >
          <input
            type="checkbox"
            className="w-4 h-4 cursor-pointer accent-blue-600 dark:accent-blue-500 pointer-events-none"
            checked={isCheckboxSelected}
            onChange={handleCheckboxClick}
            onClick={handleCheckboxClick}
          />
        </div>
      )}
    </div>
  );
};

export default BookingCard;
